import { ReactNode } from 'react'

import { Trans } from '@/components'
import { formatToDynamicDecimalPlaces } from '@/utils/formatter'
import { cn } from '@/utils/style'

interface StorageProgressBarProps {
  label: ReactNode
  usedGB: number
  totalGB: number
  percentage?: number
  color?: string
  className?: string
}

export function StorageProgressBar({
  label,
  usedGB,
  totalGB,
  percentage,
  color = 'bg-btn-tertiary',
  className,
}: StorageProgressBarProps) {
  const displayPercentage =
    percentage !== undefined
      ? percentage.toFixed(2)
      : totalGB > 0
        ? ((usedGB / totalGB) * 100).toFixed(2)
        : '0.00'

  const usagePercentage = totalGB > 0 ? (usedGB / totalGB) * 100 : 0
  const remainingPercentage = 100 - usagePercentage

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex justify-between items-center mb-4 t-sb">
        {/* Label */}
        <div className="flex gap-x-2">
          <div>{label}</div>
          <div>{displayPercentage}%</div>
        </div>

        {/* Usage Info */}
        <div className="flex items-center gap-4">
          <div className="text-secondary">
            <Trans
              i18nKey="space.usageFormat"
              values={{
                usage: formatToDynamicDecimalPlaces(usedGB),
                quota: formatToDynamicDecimalPlaces(totalGB),
              }}
              components={[
                <span className="text-primary" key="usage" />,
                <span className="text-primary" key="quota" />,
              ]}
            />
          </div>
        </div>
      </div>

      {/* Two-Segment Progress Bar */}
      <div className="w-full h-6 rounded overflow-hidden gap-1 flex">
        {usagePercentage > 0 && (
          <div className={cn('h-full rounded', color)} style={{ width: `${usagePercentage}%` }} />
        )}
        {remainingPercentage > 0 && (
          <div
            className="h-full rounded bg-neutral-200"
            style={{ width: `${remainingPercentage}%` }}
          />
        )}
      </div>
    </div>
  )
}
